'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Building2, User, Mail, Lock, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface RegisterFormData {
  businessName: string;
  fullName: string;
  email: string;
  phone: string;
  industry: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

const industries = [
  'Landscaping / Lawn Care', 'HVAC', 'Plumbing', 'Electrical',
  'Roofing', 'Restaurant / Food Service', 'Dental / Medical',
  'Real Estate', 'Law Firm', 'Beauty / Salon', 'Church / Non-Profit',
  'Retail', 'Event Venue', 'Contractor / Construction', 'Other',
];

interface RegisterFormProps {
  onSuccess: () => void;
}

export default function RegisterForm({ onSuccess }: RegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signUp } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<RegisterFormData>({
    defaultValues: {
      businessName: '', fullName: '', email: '', phone: '',
      industry: '', password: '', confirmPassword: '', agreeTerms: false,
    },
  });

  const password = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        businessName: data.businessName,
        industry: data.industry,
        phone: data.phone,
        role: 'client',
      });
      setIsLoading(false);
      toast.success('Account created! You can now sign in.');
      onSuccess();
    } catch (err: any) {
      setIsLoading(false);
      setError('email', { message: err?.message || 'Registration failed. Please try again.' });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Create your account</h2>
        <p className="text-muted-foreground text-sm mt-1">Start your 14-day free trial. No credit card required.</p>
      </div>

      {/* Business name */}
      <div>
        <label htmlFor="reg-business" className="block text-sm font-semibold text-foreground mb-1.5">
          Business Name <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            id="reg-business"
            type="text"
            placeholder="Green Ridge Lawn Care LLC"
            className={`input-field pl-9 ${errors.businessName ? 'border-red-500' : ''}`}
            {...register('businessName', { required: 'Business name is required' })}
          />
        </div>
        {errors.businessName && <p className="text-red-500 text-xs mt-1">⚠ {errors.businessName.message}</p>}
      </div>

      {/* Full name */}
      <div>
        <label htmlFor="reg-name" className="block text-sm font-semibold text-foreground mb-1.5">
          Your Full Name <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            id="reg-name"
            type="text"
            placeholder="Marcus Williams"
            className={`input-field pl-9 ${errors.fullName ? 'border-red-500' : ''}`}
            {...register('fullName', { required: 'Full name is required' })}
          />
        </div>
        {errors.fullName && <p className="text-red-500 text-xs mt-1">⚠ {errors.fullName.message}</p>}
      </div>

      {/* Email */}
      <div>
        <label htmlFor="reg-email" className="block text-sm font-semibold text-foreground mb-1.5">
          Work Email <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            id="reg-email"
            type="email"
            placeholder="marcus@greenridgelawn.com"
            className={`input-field pl-9 ${errors.email ? 'border-red-500' : ''}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
            })}
          />
        </div>
        {errors.email && <p className="text-red-500 text-xs mt-1">⚠ {errors.email.message}</p>}
      </div>

      {/* Industry */}
      <div>
        <label htmlFor="reg-industry" className="block text-sm font-semibold text-foreground mb-1.5">
          Industry <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            id="reg-industry"
            className={`input-field appearance-none pr-8 ${errors.industry ? 'border-red-500' : ''}`}
            {...register('industry', { required: 'Please select your industry' })}
          >
            <option value="">Select your industry...</option>
            {industries.map((ind) => (
              <option key={`industry-${ind}`} value={ind}>{ind}</option>
            ))}
          </select>
          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
        {errors.industry && <p className="text-red-500 text-xs mt-1">⚠ {errors.industry.message}</p>}
      </div>

      {/* Password */}
      <div>
        <label htmlFor="reg-password" className="block text-sm font-semibold text-foreground mb-1.5">
          Password <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters with at least one number</p>
        <div className="relative">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            id="reg-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Create a strong password"
            className={`input-field pl-9 pr-10 ${errors.password ? 'border-red-500' : ''}`}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Password must be at least 8 characters' },
              pattern: { value: /(?=.*[0-9])/, message: 'Password must include at least one number' },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Toggle password visibility"
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && <p className="text-red-500 text-xs mt-1">⚠ {errors.password.message}</p>}
      </div>

      {/* Confirm password */}
      <div>
        <label htmlFor="reg-confirm" className="block text-sm font-semibold text-foreground mb-1.5">
          Confirm Password <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            id="reg-confirm"
            type={showConfirm ? 'text' : 'password'}
            placeholder="Re-enter your password"
            className={`input-field pl-9 pr-10 ${errors.confirmPassword ? 'border-red-500' : ''}`}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (v) => v === password || 'Passwords do not match',
            })}
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Toggle confirm password visibility"
          >
            {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">⚠ {errors.confirmPassword.message}</p>}
      </div>

      {/* Terms */}
      <div className="flex items-start gap-2">
        <input
          id="reg-terms"
          type="checkbox"
          className="w-4 h-4 mt-0.5 rounded border-border accent-primary"
          {...register('agreeTerms', { required: 'You must agree to the terms' })}
        />
        <label htmlFor="reg-terms" className="text-sm text-muted-foreground leading-relaxed">
          I agree to the{' '}
          <a href="#" className="text-primary hover:underline font-medium">Terms of Service</a>
          {' '}and{' '}
          <a href="#" className="text-primary hover:underline font-medium">Privacy Policy</a>
        </label>
      </div>
      {errors.agreeTerms && <p className="text-red-500 text-xs">⚠ {errors.agreeTerms.message}</p>}

      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary w-full justify-center py-3 text-base"
      >
        {isLoading ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Creating your account...
          </>
        ) : (
          'Start Free Trial'
        )}
      </button>
    </form>
  );
}